"use client";

import React from "react";
import { Loader, Send } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { cn } from "@/lib/utils";
function JoinTheConvoyForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 text-center space-y-4 bg-background/20 backdrop-blur-sm rounded-lg border border-white/10 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-full bg-primary/20 flex items-center justify-center mb-2">
          <Send className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-bold text-white">Welcome to the Convoy!</h3>
        <p className="text-white/80 max-w-md">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-white/20 text-white hover:bg-white/10 hover:text-white">
          Send Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="w-full max-w-xl mx-auto space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-white/90">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" className="bg-background/10 border-white/10 text-white placeholder:text-white/40 focus-visible:ring-primary/50 focus-visible:border-primary" {...field} />
              </FormControl>
              <FormDescription className="text-white/50 text-xs">
                Enter the name you’d like us to use when contacting you.
              </FormDescription>
              <FormMessage className="text-red-300" />
            </FormItem>} />

        <FormField control={control} name="email_address" rules={{
        required: "Email Address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-white/90">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" className="bg-background/10 border-white/10 text-white placeholder:text-white/40 focus-visible:ring-primary/50 focus-visible:border-primary" {...field} />
              </FormControl>
              <FormDescription className="text-white/50 text-xs">
                We’ll send trip updates and exclusive convoy news to this address.
              </FormDescription>
              <FormMessage className="text-red-300" />
            </FormItem>} />
      </div>

      <FormField control={control} name="phone_number" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-white/90">Phone Number</FormLabel>
            <FormControl>
              <Input type="tel" placeholder="(555) 123-4567" className="bg-background/10 border-white/10 text-white placeholder:text-white/40 focus-visible:ring-primary/50 focus-visible:border-primary" {...field} />
            </FormControl>
            <FormDescription className="text-white/50 text-xs">
              Optional – for quick coordination on upcoming overland trips.
            </FormDescription>
            <FormMessage className="text-red-300" />
          </FormItem>} />

      <FormField control={control} name="travel_interest" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-white/90">
              What excites you most about joining?
            </FormLabel>
            <FormControl>
              <textarea className={cn("flex min-h-[100px] w-full rounded-md border border-white/10 bg-background/10 px-3 py-2 text-sm text-white placeholder:text-white/40 shadow-xs focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/50 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50", "resize-y")} placeholder="e.g., family-friendly routes, off-road challenges, gear swaps…" {...field} />
            </FormControl>
            <FormDescription className="text-white/50 text-xs">
              Tell us a bit about your adventure goals – helps us match you with
              the right convoy.
            </FormDescription>
            <FormMessage className="text-red-300" />
          </FormItem>} />

      {submitError && <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p className="text-red-200 text-sm">{submitError}</p>
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-bold tracking-wide bg-black text-white hover:bg-black/80 shadow-lg border border-white/10">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-5" />
              JOINING...
            </> : "SUBSCRIBE TO THE CONVOY"}
        </Button>
        <p className="text-center text-white/40 text-xs mt-4 uppercase tracking-widest">
          No spam, just dust. Get the weekly trail report.
        </p>
      </div>
    </Form>;
}
export default function JoinTheConvoy() {
  return <section id="join-the-convoy" className="dark bg-background text-foreground relative py-24 px-4 overflow-hidden">
      {}
      <div className="absolute inset-0 bg-linear-to-br from-primary via-primary/90 to-chart-5 opacity-90 z-0" />
      
      {}
      <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 mix-blend-overlay z-0 pointer-events-none" />

      <div className="relative z-10 container mx-auto max-w-4xl">
        <div className="text-center mb-12 space-y-4">
          <h2 className="text-4xl md:text-5xl font-black text-white tracking-tight uppercase drop-shadow-sm">
            Join the Convoy
          </h2>
          <p className="text-lg md:text-xl text-white/90 font-medium max-w-2xl mx-auto">
            Connect with fellow overlanders, get exclusive route guides, and gear up for your next family adventure.
          </p>
        </div>

        <div className="bg-black/20 backdrop-blur-md rounded-xl p-6 md:p-10 border border-white/10 shadow-2xl">
          <FormProvider formId="join_convoy_home" sectionName="join-the-convoy" defaultValues={{
          full_name: "",
          email_address: "",
          phone_number: "",
          travel_interest: ""
        }} validationRules={{
          full_name: {
            required: true
          },
          email_address: {
            required: true,
            pattern: "email"
          }
        }} submitText="SUBSCRIBE" successMessage="Welcome to the pack! We've received your details and will be in touch with the next trail report soon." errorMessage="We hit a bump in the road. Please check your connection and try again.">
            <JoinTheConvoyForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}